// The layout of a scrivenings run (SuiteEditor's ScriveningsLayout): fragments' text laid end to end, each preceded by
// a divider line. Per fragment the document holds `U+FFFC \n` (the divider, drawn as the fragment's title or chapter
// opener) + the fragment's text, character for character + `\n` (a terminator closing its last line, so the next
// divider starts a line of its own). Only body text belongs to a fragment; dividers and terminators are structure and
// are never edited. All offsets are UTF-16 code units, as in CodeMirror and NSString.
import type { ChangeDesc } from '@codemirror/state';
import type { FragmentKind, Opener } from '@writing-suite/core';

export const DIVIDER = '￼\n';
export const DIVIDER_LENGTH = 2;

/** What the layout knows of a fragment besides its text. */
export interface Piece {
  id: string;
  title: string;
  kind: FragmentKind;
  opener: Opener | null;
}

/** One contiguous change inside a single fragment body (`ScriveningsLayout.BodyEdit`), in document offsets. */
export interface BodyEdit { index: number; from: number; to: number; text: string }

/** Each fragment's body as [from, to) in the document, in reading order. Immutable: edits make a new one by mapping. */
export class Layout {
  constructor(readonly pieces: readonly Piece[], readonly from: readonly number[], readonly to: readonly number[],
    private readonly byId: ReadonlyMap<string, number>) {}

  static readonly empty = new Layout([], [], [], new Map());

  /** The document for `fragments` and its layout (`ScriveningsLayout.build`). */
  static build(fragments: readonly (Piece & { text: string })[]): { layout: Layout; doc: string } {
    const from: number[] = [], to: number[] = [];
    const parts: string[] = [];
    const byId = new Map<string, number>();
    let at = 0;
    fragments.forEach((f, i) => {
      parts.push(DIVIDER, f.text, '\n');
      from.push(at + DIVIDER_LENGTH);
      to.push(at + DIVIDER_LENGTH + f.text.length);
      at += DIVIDER_LENGTH + f.text.length + 1;
      if (!byId.has(f.id)) byId.set(f.id, i);
    });
    const pieces = fragments.map(({ id, title, kind, opener }) => ({ id, title, kind, opener: opener ?? null }));
    return { layout: new Layout(pieces, from, to, byId), doc: parts.join('') };
  }

  get count() { return this.pieces.length; }
  /** Where piece `i` starts: its divider. */
  start(i: number) { return this.from[i] - DIVIDER_LENGTH; }
  index(id: string): number | undefined { return this.byId.get(id); }
  kind(i: number): FragmentKind { return this.pieces[i]?.kind ?? 'prose'; }
  isScreenplay(i: number) { return i >= 0 && this.pieces[i]?.kind === 'screenplay'; }

  /** The piece holding `pos`: the last whose divider starts at or before it (the first, for a position before). */
  locate(pos: number): number {
    let lo = 0, hi = this.count - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (this.from[mid] - DIVIDER_LENGTH <= pos) lo = mid; else hi = mid - 1;
    }
    return lo;
  }

  /** The body `pos` is in (or at either end of), else −1: a divider (`ScriveningsLayout.region`). A position past the
   *  end of the run is the end of the last body. */
  bodyAt(pos: number): number {
    if (this.count === 0) return -1;
    const i = this.locate(pos);
    return pos >= this.from[i] ? i : -1;
  }

  /** The nearest caret position in a body: a divider sends the caret to the body after it (`snap`). */
  snap(pos: number): number {
    if (this.count === 0) return 0;
    const i = this.locate(pos);
    if (pos < this.from[i]) return this.from[i];
    return Math.min(pos, this.to[i]);
  }

  /** The body holding all of [a, b], else −1 (`isWithinOneFragment`). */
  within(a: number, b: number): number {
    if (this.count === 0) return -1;
    const i = this.locate(a);
    return a >= this.from[i] && b <= this.to[i] ? i : -1;
  }

  /** Splits a replacement of [a, b) into per-body edits; the text goes into the first body touched, dividers and
   *  terminators are kept (`ScriveningsLayout.plan`). Empty when it touches only structure and inserts nothing. */
  plan(a: number, b: number, text: string): BodyEdit[] {
    if (this.count === 0) return [];
    if (a === b) {
      const p = this.snap(a);
      return [{ index: this.locate(p), from: p, to: p, text }];
    }
    const edits: BodyEdit[] = [];
    for (let i = this.locate(a); i < this.count; i++) {
      if (this.from[i] >= b) break;
      const lo = Math.max(a, this.from[i]), hi = Math.min(b, this.to[i]);
      if (lo < hi) edits.push({ index: i, from: lo, to: hi, text: edits.length === 0 ? text : '' });
    }
    if (edits.length === 0 && text) return this.plan(a, a, text);
    return edits;
  }

  /** The layout after `changes` (each change inside one body): bodies before the first change keep their offsets, the
   *  rest map through the change set; text inserted at a body's edge belongs to that body. */
  map(changes: ChangeDesc): Layout {
    if (changes.empty || this.count === 0) return this;
    let first = -1;
    changes.iterChangedRanges((fromA) => { if (first < 0) first = fromA; }, false);
    const k = Math.max(0, this.locate(Math.max(0, first)) - 1);
    const from = this.from.slice(), to = this.to.slice();
    for (let i = k; i < this.count; i++) {
      from[i] = changes.mapPos(this.from[i], -1);
      to[i] = changes.mapPos(this.to[i], 1);
    }
    return new Layout(this.pieces, from, to, this.byId);
  }

  /** The same bodies with new titles, kinds or openers (same ids, same order). */
  withPieces(pieces: readonly Piece[]): Layout {
    return new Layout(pieces, this.from, this.to, this.byId);
  }
}
